import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createThresholdApi,
  getStockItemDetailsApi,
  getStockItemsListApi,
  getStoreThresholdsApi,
  getThresholdsListApi,
  updateBulkVisibilityApi,
  updateStoreBulkVisibilityApi,
  updateStoreThresholdApi,
  updateStoreVisibilityApi,
} from "../api";
import { successMessage, errorMessage } from "../../../utils/helper";

export const stockItemsKeys = {
  all: ["stockItems"],
  lists: () => [...stockItemsKeys.all, "list"],
  list: (params) => [...stockItemsKeys.lists(), params],
  detail: (id) => [...stockItemsKeys.lists(), id],
  storeThresholds: (stockId) => [
    ...stockItemsKeys.all,
    "storeThresholds",
    stockId,
  ],
  bulkUpdate: (stockId) => [...stockItemsKeys.all, "bulkUpdate", stockId],
};

export const useStockItemsListQuery = (params = {}) => {
  const isStoreAdmin = window.location.pathname.includes("/store-admin");
  return useQuery({
    queryKey: stockItemsKeys.list({ ...params, isStoreAdmin }),
    queryFn: () =>
      isStoreAdmin
        ? getThresholdsListApi(params)
        : getStockItemsListApi(params),
    select: (data) => data?.data,
  });
};

export const useStockItemDetailsQuery = (itemId) => {
  return useQuery({
    queryKey: stockItemsKeys.detail(itemId),
    queryFn: () => getStockItemDetailsApi(itemId),
    enabled: itemId > 0,
  });
};

export const useStoreThresholdsQuery = (stockId) => {
  return useQuery({
    queryKey: stockItemsKeys.storeThresholds(stockId),
    queryFn: () => getStoreThresholdsApi(stockId),
    select: (data) =>
      data?.data?.map((item) => ({ ...item, id: item.threshold_id })),
    enabled: stockId > 0,
  });
};

export const useUpdateStoreThresholdMutation = (stockId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params) => updateStoreThresholdApi(params),
    onSuccess: (responseData) => {
      successMessage(responseData?.message);
      queryClient.invalidateQueries({
        queryKey: stockItemsKeys.storeThresholds(stockId),
      });
    },
    onError: (error) => {
      errorMessage(
        error?.response?.data?.message || "Failed to update threshold",
      );
    },
  });
};

export const useUpdateStoreVisibilityMutation = (stockId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params) => updateStoreVisibilityApi(params),
    onSuccess: (responseData) => {
      successMessage(responseData?.message);
      const updatedItem = responseData?.data;
      if (!updatedItem) return;
      queryClient.setQueryData(
        stockItemsKeys.storeThresholds(stockId),
        (old) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: old.data.map((item) =>
              item.store_id === updatedItem.store_id
                ? { ...item, visibility: updatedItem.visibility }
                : item,
            ),
          };
        },
      );
    },
    onError: (error) => {
      errorMessage(
        error?.response?.data?.message || "Failed to update visibility",
      );
    },
  });
};

export const useCreateThresholdMutation = () => {
  const queryClient = useQueryClient();
  const isStoreAdmin = window.location.pathname.includes("/store-admin");
  return useMutation({
    mutationFn: (formData) => createThresholdApi(formData),
    onSuccess: (responseData) => {
      successMessage(responseData?.message);
      const updatedItem = responseData?.data;
      if (!updatedItem) return;
      // Update the cached list in-place — no refetch needed
      const queryKey = stockItemsKeys.list({ isStoreAdmin });
      queryClient.setQueryData(queryKey, (old) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.map((item) =>
            item.id === updatedItem.id ? updatedItem : item,
          ),
        };
      });
    },
    onError: (error) => {
      errorMessage(
        error?.response?.data?.message || "Failed to save threshold",
      );
    },
  });
};

export const useUpdateStoreBulkVisibilityMutation = (stockId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params) => updateStoreBulkVisibilityApi(params),
    onSuccess: (responseData) => {
      successMessage(responseData?.message);
      queryClient.invalidateQueries({
        queryKey: stockItemsKeys.storeThresholds(stockId),
      });
    },
    onError: (error) => {
      errorMessage(
        error?.response?.data?.message || "Failed to update bulk visibility",
      );
    },
  });
};

export const useUpdateBulkVisibilityMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params) => updateBulkVisibilityApi(params),
    onSuccess: (responseData) => {
      successMessage(responseData?.message);
      queryClient.invalidateQueries({
        queryKey: stockItemsKeys.list({ isStoreAdmin: true }), // bulk visibility update is relevant for store admin view
      });
    },
    onError: (error) => {
      errorMessage(
        error?.response?.data?.message || "Failed to update bulk visibility",
      );
    },
  });
};
